import React, { useState, useRef, useEffect } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { todayInUAEDate } from "../utils/timezone";

const DateRangePicker = ({ value, onChange, placeholder = "Select Date Range", className = "" }) => {
    const [startDate, setStartDate] = useState(value && value[0] ? value[0] : null);
    const [endDate, setEndDate] = useState(value && value[1] ? value[1] : null);
    const [showPresets, setShowPresets] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        // Update local state when value prop changes
        if (value) {
            setStartDate(value[0] || null);
            setEndDate(value[1] || null);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowPresets(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        
        if (onChange) {
            onChange([start, end]);
        }
    };

    // Helper functions for date manipulation
    const startOfDay = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const endOfDay = (date) => {
        const d = new Date(date);
        d.setHours(23, 59, 59, 999);
        return d;
    };

    const subDays = (date, days) => {
        const d = new Date(date);
        d.setDate(d.getDate() - days);
        return d;
    };

    const startOfWeek = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
        const result = new Date(d);
        result.setDate(diff);
        return result;
    };

    const endOfWeek = (date) => {
        const d = startOfWeek(date);
        d.setDate(d.getDate() + 6);
        return endOfDay(d);
    };

    const startOfMonth = (date) => {
        const d = new Date(date);
        d.setDate(1);
        return startOfDay(d);
    };

    const endOfMonth = (date) => {
        const d = new Date(date);
        d.setMonth(d.getMonth() + 1);
        d.setDate(0);
        return endOfDay(d);
    };

    const applyPreset = (preset) => {
        const today = todayInUAEDate();
        let start, end;

        switch (preset) {
            case 'yesterday':
                start = startOfDay(subDays(today, 1));
                end = endOfDay(subDays(today, 1));
                break;
            case 'lastWeek':
                start = startOfDay(startOfWeek(subDays(today, 7)));
                end = endOfWeek(subDays(today, 7));
                break;
            case 'lastMonth':
                const lastMonthDate = subDays(today, 30);
                start = startOfMonth(lastMonthDate);
                end = endOfMonth(lastMonthDate);
                break;
            case 'custom':
                setShowPresets(false);
                return;
            default:
                return;
        }

        setStartDate(start);
        setEndDate(end);
        if (onChange) {
            onChange([start, end]);
        }
        setShowPresets(false);
    };

    const clearDates = () => {
        setStartDate(null);
        setEndDate(null);
        if (onChange) {
            onChange([null, null]);
        }
    };

    return (
        <div className={`position-relative ${className}`} ref={dropdownRef}>
            <div className="d-flex gap-2">
                <div className="flex-grow-1 position-relative">
                    <DatePicker
                        selectsRange={true}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={handleDateChange}
                        isClearable={true}
                        className="form-control form-control-solid"
                        placeholderText={placeholder}
                        dateFormat="dd/MM/yyyy"
                    />
                </div>
                <div className="position-relative">
                    <button
                        type="button"
                        className="btn btn-light btn-sm"
                        onClick={() => setShowPresets(!showPresets)}
                        style={{ minWidth: '120px' }}
                    >
                        <i className="bi bi-calendar3 me-2"></i>
                        Date Range
                        <i className={`bi bi-chevron-${showPresets ? 'up' : 'down'} ms-2`}></i>
                    </button>
                    {showPresets && (
                        <div 
                            className="dropdown-menu show position-absolute"
                            style={{ 
                                top: '100%', 
                                right: 0, 
                                marginTop: '4px',
                                zIndex: 1000,
                                minWidth: '180px'
                            }}
                        >
                            <button
                                className="dropdown-item"
                                type="button"
                                onClick={() => applyPreset('yesterday')}
                            >
                                <i className="bi bi-calendar-day me-2"></i>Yesterday
                            </button>
                            <button
                                className="dropdown-item"
                                type="button"
                                onClick={() => applyPreset('lastWeek')}
                            >
                                <i className="bi bi-calendar-week me-2"></i>Last Week
                            </button>
                            <button
                                className="dropdown-item"
                                type="button"
                                onClick={() => applyPreset('lastMonth')}
                            >
                                <i className="bi bi-calendar-month me-2"></i>Last Month
                            </button>
                            <div className="dropdown-divider"></div>
                            <button
                                className="dropdown-item"
                                type="button"
                                onClick={clearDates}
                            >
                                <i className="bi bi-x-circle me-2"></i>Clear
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DateRangePicker;

